import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import { Button, Tooltip, CircularProgress } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ShopContext } from 'Context/ShopContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import PrintIcon from '@mui/icons-material/Print';
import { PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useLocation, useParams } from 'react-router-dom';
import SalesPersonService from 'services/SalesPersonService';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

function SalespersonDetailedReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(new Date(queryParams.get('filterFromDate')));
  const [filterToDate, setFilterToDate] = useState(new Date(queryParams.get('filterToDate')));
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const [searchText, setSearchText] = useState('');
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    item_id: tCommon('ID'),
    item_name: tCommon('Name'),
    total_quantity: tCommon('Quantity'),
    total_amount: tCommon('Amount'),
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return filterFromDate !== prevFilterData.filterFromDate || filterToDate !== prevFilterData.filterToDate;
  }, [filterFromDate, filterToDate, prevFilterData.filterFromDate, prevFilterData.filterToDate]);

  useEffect(() => {
    generateSummeryReport();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Salesperson Detailed Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredItems,
      {
        [tCommon('Total Quantity')]: summary.totalQty,
        [tCommon('Total Amount')]: summary.totalAmount,
      }
    );
  };

  const generateSummeryReport = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('SALESPERSON_SUMMARY');
    setFilterFromDate(dynamicStartDate);
    setFilterToDate(dynamicEndDate);

    loadBillSummaryReports();
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: dynamicStartDate,
      filterToDate: dynamicEndDate,
    });
  };

  const loadBillSummaryReports = async ({ startTime, endTime } = {}) => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('SALESPERSON_SUMMARY');

    const start = startTime || dynamicStartDate;
    const end = endTime || dynamicEndDate;

    if (DateTimeHelpers.reportDateLimit(start, end)) {
      setDateLimitPopup(true);
      return;
    }

    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(start),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(end),
      };

      const resp = await SalesPersonService.getSalespersonDetailedReport(id, data);
      setItems(resp);
      setFilteredItems(resp);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }

    setLoadingIndicator(false);
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(items, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    saveAs(blob, `sales-${new Date().toISOString()}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setFilteredItems(items.filter((item) => item.name.toLowerCase().includes(value.toLowerCase())));
  };

  const _getSalespersonReportSummary = (items) => {
    let totalQty = 0;
    let totalAmount = 0;
    items.forEach((item) => {
      totalAmount += item.total_amount;
      totalQty += item.total_quantity;
    });

    return {
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalQty: totalQty,
    };
  };

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'item_id',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'item_name',
      type: 'text',
    },
    {
      label: tCommon('Date'),
      id: 'Date',
      type: 'callback',
      viewRender: (item) => {
        return item.time ? DateTimeHelpers.getDate(item.time) : '';
      },
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => {
        return item.time ? DateTimeHelpers.convertTo12HourTime(item.time) : '';
      },
    },
    {
      label: tCommon('Quantity'),
      id: 'total_quantity',
      type: 'text',
    },
    {
      label: tCommon('Amount'),
      id: 'total_amount',
      type: 'text',
    },
  ];

  const summary = _getSalespersonReportSummary(filteredItems);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec} style={{ alignItems: 'flex-start' }}>
        <IconButton
          className={styles.backBtn}
          onClick={() => {
            window.history.back();
          }}
        >
          <ArrowBackRoundedIcon />
        </IconButton>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport("Service Staff '{{salesperson}}' Detailed Report for {{from}} to {{to}}", {
                salesperson: queryParams.get('salesperson'),
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  handleSearch(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tCommon('Search by name')}
              />
              <Tooltip title="Search" placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="SALESPERSON_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateSummeryReport}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        items &&
        (filteredItems && filteredItems.length ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredItems ? filteredItems : items}
              rowKey="id"
              summary={{
                name: tCommon('Total'),
                total_quantity: summary.totalQty,
                total_amount: summary.totalAmount,
              }}
            />
          </div>
        ) : (
          !loadingIndicator && (
            <Info
              severity="warning"
              title={tReport('Oh no, there are no sales matching to your filter')}
              content={tReport(
                'There are no sales matching to your filter. Please verify your inputs to generate report.'
              )}
            />
          )
        ))
      )}

      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(SalespersonDetailedReport);
